import type {
  StreamLainAnimationHandle,
  StreamLainMotionOutcome,
} from './streamlain-motion-curves';
import type { StreamLainMotionDomain } from './streamlain-motion-policy';

/** Dependency-free native controller, also embedded before the app bundle boots. */
export function createStreamLainCompositorController(
  policy: {
    isEnabled: (domain: StreamLainMotionDomain) => boolean;
    subscribe: (listener: () => void) => () => void;
  } = { isEnabled: () => true, subscribe: () => () => {} }
) {
  const active = new WeakMap<Element, StreamLainAnimationHandle>();
  const cancel = (element: Element) => active.get(element)?.cancel();
  const isEnabled = (
    element: Element,
    domain: StreamLainMotionDomain = 'app'
  ) => {
    const view = element.ownerDocument.defaultView;
    return (
      !!view &&
      !view.matchMedia('(prefers-reduced-motion: reduce)').matches &&
      policy.isEnabled(domain)
    );
  };
  const play = (
    element: Element,
    keyframes: Keyframe[],
    timing: KeyframeAnimationOptions,
    domain: StreamLainMotionDomain = 'app',
    startTime?: number | null
  ): StreamLainAnimationHandle => {
    cancel(element);
    const view = element.ownerDocument.defaultView;
    if (!view)
      throw new Error('Motion requires a document with a browsing context');
    const preference = view.matchMedia('(prefers-reduced-motion: reduce)');
    if (!isEnabled(element, domain)) {
      return { finished: Promise.resolve('finished'), cancel() {} };
    }
    const animation = element.animate(keyframes, timing);
    if (startTime != null) animation.startTime = startTime;
    let settle!: (outcome: StreamLainMotionOutcome) => void;
    const finished = new Promise<StreamLainMotionOutcome>(resolve => {
      settle = resolve;
    });
    let settled = false;
    let unsubscribe = () => {};
    const complete = (outcome: StreamLainMotionOutcome) => {
      if (settled) return;
      settled = true;
      preference.removeEventListener('change', onPreferenceChange);
      unsubscribe();
      if (active.get(element) === handle) active.delete(element);
      animation.cancel();
      settle(outcome);
    };
    const onPreferenceChange = () => {
      if (preference.matches || !policy.isEnabled(domain)) complete('finished');
    };
    const handle: StreamLainAnimationHandle = {
      finished,
      cancel: () => complete('cancelled'),
    };
    active.set(element, handle);
    preference.addEventListener('change', onPreferenceChange);
    unsubscribe = policy.subscribe(onPreferenceChange);
    void animation.finished.then(
      () => complete('finished'),
      () => complete('cancelled')
    );
    return handle;
  };
  const playLoop = (
    element: Element,
    keyframes: Keyframe[],
    durationMs: number,
    startTime?: number | null
  ) =>
    play(
      element,
      keyframes,
      {
        duration: durationMs,
        iterations: Infinity,
        easing: 'linear',
        fill: 'both',
      },
      'app',
      startTime
    );
  return { cancel, isEnabled, play, playLoop };
}
